import { useEffect, useState } from "react";
import { appMode, pb } from "./pocketbase";

/**
 * What goes at the top of anything a client sees.
 *
 * The durable copy is the `business` JSON field on the signed-in user record,
 * so it follows you to every browser and device — and Reimburse reads the same
 * field for the "Submitted by" name on its package (it writes only `name`,
 * merging into the rest). This browser keeps a cached copy so a printed report
 * renders its letterhead without waiting on the server, and so the demo, which
 * has no server, still works.
 */
export interface Business {
  name: string;
  email: string;
  address: string;
  taxId: string;
  /** Payment terms, remittance details — printed under the table. */
  notes: string;
  /**
   * A data URL, not a link.
   *
   * A hosted image would be one more thing that can 404 on the day you send an
   * invoice, and the print dialog would race the fetch. Inlined, the logo is
   * part of the document. Kept small deliberately — see the upload guard.
   */
  logo: string;
}

const BUSINESS_KEY = "ph.business";
const EMPTY_BUSINESS: Business = { name: "", email: "", address: "", taxId: "", notes: "", logo: "" };
const listeners = new Set<(v: Business) => void>();

function normalize(raw: unknown): Business {
  const r = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const out = { ...EMPTY_BUSINESS };
  for (const k of Object.keys(EMPTY_BUSINESS) as (keyof Business)[]) {
    if (typeof r[k] === "string") out[k] = r[k] as string;
  }
  return out;
}

const isEmpty = (b: Business) => Object.values(b).every((v) => !v.trim());

export function loadBusiness(): Business {
  try { return normalize(JSON.parse(localStorage.getItem(BUSINESS_KEY) || "null")); } catch { return EMPTY_BUSINESS; }
}

function writeLocal(next: Business): void {
  try { localStorage.setItem(BUSINESS_KEY, JSON.stringify(next)); } catch { /* private mode — server copy stands */ }
  for (const fn of listeners) fn(next);
}

// ---- Server copy ------------------------------------------------------------

/** null = not attempted yet; false = the server dropped the field. */
let persistedOnServer: boolean | null = null;
const persistListeners = new Set<(v: boolean | null) => void>();
function setPersisted(v: boolean): void {
  persistedOnServer = v;
  for (const fn of persistListeners) fn(v);
}

/**
 * The fields save on every keystroke, so the server write is debounced. The
 * revision counters stop a reply to an older write — which the SDK copies into
 * the auth store, which calls adoptAccountBusiness — from overwriting what you
 * have typed since.
 */
let localRev = 0;
let savedRev = 0;
let timer: ReturnType<typeof setTimeout> | null = null;

function scheduleServerWrite(): void {
  if (timer) clearTimeout(timer);
  timer = setTimeout(() => { timer = null; void writeServer(); }, 600);
}

async function writeServer(): Promise<void> {
  const userId = (pb?.authStore?.model as { id?: string } | null)?.id;
  if (appMode === "demo" || !userId) return;
  const rev = localRev;
  try {
    const saved = await pb.collection("users").update(userId, { business: loadBusiness() });
    // A collection without the field returns the record without it.
    setPersisted((saved as Record<string, unknown>).business != null);
  } catch {
    /* offline or refused — the local copy already applied; the next edit retries */
    return;
  }
  savedRev = Math.max(savedRev, rev);
}

/**
 * Adopt the details stored on the signed-in account (called from AuthContext
 * on every auth change, like the theme and calendars). The account copy wins
 * when it holds anything; when it holds nothing but this browser does —
 * details entered before the field existed — this browser's copy is lifted up
 * once.
 */
let lifted = false;
export function adoptAccountBusiness(record: Record<string, unknown> | null): void {
  if (!record || localRev !== savedRev) return;
  const server = normalize(record.business);
  if (!isEmpty(server)) {
    if (JSON.stringify(server) !== JSON.stringify(loadBusiness())) writeLocal(server);
    persistedOnServer = true;
    return;
  }
  if (!lifted && !isEmpty(loadBusiness())) {
    lifted = true;
    void writeServer();
  }
}

export function useBusiness(): [Business, (patch: Partial<Business>) => void, boolean | null] {
  const [value, setValue] = useState<Business>(loadBusiness);
  const [persisted, setPersistedState] = useState<boolean | null>(persistedOnServer);
  useEffect(() => {
    listeners.add(setValue);
    persistListeners.add(setPersistedState);
    return () => { listeners.delete(setValue); persistListeners.delete(setPersistedState); };
  }, []);
  const save = (patch: Partial<Business>) => {
    writeLocal({ ...loadBusiness(), ...patch });
    localRev++;
    scheduleServerWrite();
  };
  return [value, save, persisted];
}
